/**
 * Color derivation utilities for particle effects.
 *
 * Replaces hardcoded fire/cyan hex values with functions that derive
 * warm/cool/dark/light variants from any weapon color. Keeps Sakurai
 * choreography intact — same timing, same counts, just correct colors.
 */

// ── Helpers ──

/** Extract RGB channels from a hex color */
function toRGB(hex: number): [number, number, number] {
  return [(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff];
}

/** Pack RGB channels back to hex */
function fromRGB(r: number, g: number, b: number): number {
  return ((Math.round(r) & 0xff) << 16) | ((Math.round(g) & 0xff) << 8) | (Math.round(b) & 0xff);
}

/** Clamp value 0-255 */
function clamp(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** Convert RGB to HSL (h: 0-360, s: 0-1, l: 0-1) */
function rgbToHSL(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return [h, s, l];
}

/** Convert HSL to RGB */
function hslToRGB(h: number, s: number, l: number): [number, number, number] {
  h = ((h % 360) + 360) % 360;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hN = h / 360;
  return [
    Math.round(hue2rgb(p, q, hN + 1/3) * 255),
    Math.round(hue2rgb(p, q, hN) * 255),
    Math.round(hue2rgb(p, q, hN - 1/3) * 255),
  ];
}

// ── Public API ──

/** Shift toward black. amount 0=unchanged, 1=black */
export function darken(color: number, amount: number): number {
  const [r, g, b] = toRGB(color);
  const t = 1 - amount;
  return fromRGB(r * t, g * t, b * t);
}

/** Shift toward white. amount 0=unchanged, 1=white */
export function lighten(color: number, amount: number): number {
  const [r, g, b] = toRGB(color);
  return fromRGB(
    clamp(r + (255 - r) * amount),
    clamp(g + (255 - g) * amount),
    clamp(b + (255 - b) * amount),
  );
}

/** Shift hue toward orange (30°). amount 0=unchanged, 1=full orange hue */
export function warm(color: number, amount: number): number {
  const [r, g, b] = toRGB(color);
  let [h, s, l] = rgbToHSL(r, g, b);
  // Lerp hue toward 30° (orange) via shortest path
  const target = 30;
  let diff = target - h;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  h += diff * amount;
  // Boost saturation slightly for combustion feel
  s = Math.min(1, s + amount * 0.15);
  const [nr, ng, nb] = hslToRGB(h, s, l);
  return fromRGB(nr, ng, nb);
}

/** Shift hue toward cyan-blue (195°). amount 0=unchanged, 1=full cyan hue */
export function cool(color: number, amount: number): number {
  const [r, g, b] = toRGB(color);
  let [h, s, l] = rgbToHSL(r, g, b);
  const target = 195;
  let diff = target - h;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  h += diff * amount;
  s = Math.min(1, s + amount * 0.1);
  const [nr, ng, nb] = hslToRGB(h, s, l);
  return fromRGB(nr, ng, nb);
}

/** Reduce saturation. amount 0=unchanged, 1=fully grey */
function desaturate(color: number, amount: number): number {
  const [r, g, b] = toRGB(color);
  let [h, s, l] = rgbToHSL(r, g, b);
  s *= (1 - amount);
  const [nr, ng, nb] = hslToRGB(h, s, l);
  return fromRGB(nr, ng, nb);
}

/**
 * Generate an 8-shade gem palette from any base color.
 * Produces: base, lighter, darker, warmer, cooler, desaturated, bright, deep.
 * Replaces the static GEM_PALETTES lookup.
 */
export function generatePalette(color: number): number[] {
  return [
    color,                             // base identity
    lighten(color, 0.35),              // bright variant
    lighten(color, 0.55),              // very bright / white-ish
    darken(color, 0.25),               // darker shade
    darken(color, 0.5),                // deep shade
    warm(color, 0.25),                 // warm accent
    cool(color, 0.2),                  // cool accent
    desaturate(lighten(color, 0.2), 0.3), // pastel / washed
  ];
}
